(function () {
  'use strict';

  const SECTION_ID = 'cart-drawer';

  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.rootUrl = this.dataset.rootUrl || '/';
      this.addEventListener('click', this.handleClick.bind(this));
      this.addEventListener('change', this.handleChange.bind(this));
    }

    connectedCallback() {
      // Guarantee this sits directly on <body>. If a theme section include
      // ends up nested inside a header/wrapper that has a CSS transform,
      // filter, perspective, or "overflow" set, `position: fixed` on a
      // descendant gets trapped inside that ancestor's box instead of the
      // viewport — which is what causes the drawer to render clipped
      // behind/inside the header. Moving it to <body> sidesteps that.
      if (this.parentElement !== document.body) {
        document.body.appendChild(this);
      }

      this._openHandler = () => this.open();
      this._closeHandler = () => this.close();
      document.addEventListener('cart-drawer:open', this._openHandler);
      document.addEventListener('cart-drawer:close', this._closeHandler);
      document.addEventListener('keydown', (this._escHandler = (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
      }));
      if (this.dataset.reservationMinutes) this.startTimer();
    }

    disconnectedCallback() {
      document.removeEventListener('cart-drawer:open', this._openHandler);
      document.removeEventListener('cart-drawer:close', this._closeHandler);
      document.removeEventListener('keydown', this._escHandler);
    }

    handleClick(event) {
      if (event.target.closest('[data-ccd-close]')) {
        this.close();
        return;
      }

      const removeBtn = event.target.closest('[data-ccd-remove]');
      if (removeBtn) {
        event.preventDefault();
        const item = removeBtn.closest('[data-ccd-item]');
        this.updateLine(item.dataset.key, 0);
        return;
      }

      const minus = event.target.closest('[data-ccd-qty-minus]');
      const plus = event.target.closest('[data-ccd-qty-plus]');
      if (minus || plus) {
        const item = event.target.closest('[data-ccd-item]');
        const input = item.querySelector('[data-ccd-qty-input]');
        let qty = parseInt(input.value, 10) || 0;
        qty = plus ? qty + 1 : Math.max(0, qty - 1);
        input.value = qty;
        this.updateLine(item.dataset.key, qty);
        return;
      }

      const offerAdd = event.target.closest('[data-ccd-offer-add]');
      if (offerAdd && !offerAdd.disabled) {
        this.quickAddVariant(offerAdd, 'offer add failed');
        return;
      }

      const recAdd = event.target.closest('[data-ccd-rec-add]');
      if (recAdd && !recAdd.disabled) {
        this.quickAddVariant(recAdd, 'recommendation add failed');
        return;
      }

      const recsPrev = event.target.closest('[data-ccd-recs-prev]');
      const recsNext = event.target.closest('[data-ccd-recs-next]');
      if (recsPrev || recsNext) {
        const track = this.querySelector('[data-ccd-recs-track]');
        if (track) {
          const amount = track.clientWidth * 0.8;
          track.scrollBy({ left: recsPrev ? -amount : amount, behavior: 'smooth' });
        }
      }
    }

    // Shared by the single "offer" upsell and each "You may also like" card:
    // both are a single-variant, single-click add with no options to pick.
    async quickAddVariant(button, errorLabel) {
      const variantId = button.dataset.variantId;
      if (!variantId) return;
      button.disabled = true;
      const originalLabel = button.textContent;
      button.textContent = 'Adding…';

      try {
        const res = await fetch(`${this.rootUrl}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });
        const result = await res.json();

        if (!res.ok) {
          document.dispatchEvent(new CustomEvent('cart:error', { detail: result }));
          button.disabled = false;
          button.textContent = originalLabel;
          return;
        }

        button.classList.add('is-added');
        button.textContent = 'Added';
        await this.refresh();
      } catch (err) {
        console.error(`[cart-drawer] ${errorLabel}`, err);
        button.disabled = false;
        button.textContent = originalLabel;
      }
    }

    handleChange(event) {
      const input = event.target.closest('[data-ccd-qty-input]');
      if (!input) return;
      const item = input.closest('[data-ccd-item]');
      const qty = Math.max(0, parseInt(input.value, 10) || 0);
      this.updateLine(item.dataset.key, qty);
    }

    async updateLine(key, quantity) {
      this.classList.add('is-loading');
      try {
        const res = await fetch(`${this.rootUrl}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity }),
        });
        const cart = await res.json();
        await this.refresh(cart);
      } catch (err) {
        console.error('[cart-drawer] update failed', err);
      } finally {
        this.classList.remove('is-loading');
      }
    }

    async refresh(cart) {
      const wasOpen = this.classList.contains('is-open');
      const res = await fetch(`${this.rootUrl}?sections=${SECTION_ID}`);
      const data = await res.json();
      const html = data[SECTION_ID];
      if (!html) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('cart-drawer');
      if (!fresh) return;

      this.innerHTML = fresh.innerHTML;
      this.dataset.cartTotal = fresh.dataset.cartTotal;
      if (wasOpen) this.open();
      // The countdown element was just replaced along with everything else
      // in innerHTML, so re-attach the ticking interval to the new node
      // rather than letting the old interval keep updating a detached one.
      if (this.dataset.reservationMinutes) this.startTimer();

      document.dispatchEvent(
        new CustomEvent('cart:updated', { detail: { cart: cart || null } })
      );
    }

    startTimer() {
      const el = this.querySelector('[data-cart-timer]');
      if (!el) return;
      clearInterval(this._timerInterval);

      const key = 'cartDrawerReservedUntil';
      const minutes = parseInt(this.dataset.reservationMinutes, 10) || 10;
      let until = parseInt(sessionStorage.getItem(key) || '0', 10);
      if (!until || until < Date.now()) {
        until = Date.now() + minutes * 60 * 1000;
        sessionStorage.setItem(key, String(until));
      }

      const tick = () => {
        const remaining = Math.max(0, until - Date.now());
        const m = Math.floor(remaining / 60000);
        const s = Math.floor((remaining % 60000) / 1000);
        el.textContent = `${m}:${String(s).padStart(2, '0')}`;
        if (remaining <= 0) clearInterval(this._timerInterval);
      };
      tick();
      this._timerInterval = setInterval(tick, 1000);
    }

    open() {
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('cart-drawer-open');
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('cart-drawer-open');
    }
  }

  customElements.define('cart-drawer', CartDrawer);

  // ---------------------------------------------------------------------
  // Global redirect-to-drawer behavior.
  // Anything that would normally send the shopper to /cart, or add an
  // item and land on /cart, opens the drawer in place instead.
  // ---------------------------------------------------------------------

  function getDrawer() {
    return document.getElementById('cart-drawer');
  }

  function isCartLink(anchor) {
    if (!anchor || !anchor.href) return false;
    try {
      const url = new URL(anchor.href, window.location.origin);
      // Matches /cart, /cart/, or a localized "/en/cart" etc.
      return /\/cart\/?$/.test(url.pathname) && !url.pathname.includes('/cart/add');
    } catch (e) {
      return false;
    }
  }

  function isAddToCartForm(form) {
    return form && form.tagName === 'FORM' && /\/cart\/add(\.js)?(\?|$)/.test(form.action);
  }

  // Intercept clicks on any link pointing at the cart page. Also on the
  // capture phase, for the same reason as the submit listener below: it
  // preempts any theme click handler bound directly to the cart icon.
  document.addEventListener(
    'click',
    (event) => {
      const anchor = event.target.closest('a');
      if (!anchor) return;
      if (isCartLink(anchor)) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        document.dispatchEvent(new CustomEvent('cart-drawer:open'));
      }
    },
    true
  );

  // Intercept "Add to cart" form submissions and add via AJAX instead of
  // letting the browser navigate to the cart page.
  //
  // This listener is registered on the CAPTURE phase and calls
  // stopImmediatePropagation() before doing anything else. That matters:
  // most themes (including this one) have their own product-form handler
  // that also listens for "submit", does its own AJAX add, and shows its
  // own "Added to your cart" banner. Capturing on `document` runs before
  // that handler ever sees the event, so this stops the theme's own
  // notification from firing alongside our drawer — only one add happens,
  // and only the drawer opens.
  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target;
      if (!isAddToCartForm(form)) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const drawer = getDrawer();
      const submitter = event.submitter;
      if (submitter) submitter.disabled = true;

      try {
        const formData = new FormData(form);
        const rootUrl = (drawer && drawer.dataset.rootUrl) || '/';
        const res = await fetch(`${rootUrl}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
        });
        const result = await res.json();

        if (!res.ok) {
          document.dispatchEvent(new CustomEvent('cart:error', { detail: result }));
          return;
        }

        if (drawer) {
          await drawer.refresh();
          drawer.open();
        }
      } catch (err) {
        console.error('[cart-drawer] add to cart failed', err);
      } finally {
        if (submitter) submitter.disabled = false;
      }
    },
    true
  );

  // If a shopper lands directly on the /cart URL (typed it, old bookmark,
  // back button, etc.) open the drawer automatically instead of showing
  // the plain cart page underneath it.
  document.addEventListener('DOMContentLoaded', () => {
    if (window.location.pathname.replace(/\/$/, '').endsWith('/cart')) {
      document.dispatchEvent(new CustomEvent('cart-drawer:open'));
    }
  });
})();